import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, User, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

export function Login() {
  const { login, user } = useApp();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || null;
  const [email, setEmail] = useState(sp.get("email") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // If an already-authenticated user lands on /login, send them to their
  // role dashboard instead of showing the sign-in form.
  React.useEffect(() => {
    if (user) {
      nav(next || (user.role === "admin" ? "/admin" : user.role === "cleaner" ? "/cleaner" : "/dashboard"), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name}`);
      if (next) nav(next);
      else nav(u.role === "admin" ? "/admin" : u.role === "cleaner" ? "/cleaner" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <Card className="max-w-md w-full border-slate-200">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-2xl bg-teal-600 mx-auto flex items-center justify-center teal-glow">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-display font-bold text-2xl mt-4 text-slate-900">Welcome back</h2>
            <p className="text-sm text-slate-500">Sign in to your CLEANBAZ account</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-1.5" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="mt-1.5" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
          <div className="text-center mt-3">
            <Link to="/forgot-password" className="text-sm text-teal-600 hover:text-teal-700 font-semibold" data-testid="login-forgot-password-link">
              Forgot password?
            </Link>
          </div>
          <div className="text-center text-sm text-slate-600 mt-5">
            Don't have an account? <Link to="/signup" className="text-teal-600 font-semibold" data-testid="login-to-signup">Sign up</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function Signup() {
  const { signup, user } = useApp();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialRole = sp.get("role") === "cleaner" ? "cleaner" : "customer";
  const next = sp.get("next");
  const refFromUrl = sp.get("ref") || "";
  const [role, setRole] = useState(initialRole);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [refCode, setRefCode] = useState(refFromUrl);
  const [loading, setLoading] = useState(false);

  // If an already-authenticated user lands on /signup, redirect: a customer
  // wanting to also be a cleaner sees a toast pointing them at the account
  // upgrade page (single-account rule — no duplicate customer/cleaner
  // accounts allowed). Otherwise route to their role dashboard.
  React.useEffect(() => {
    if (!user) return;
    if (initialRole === "cleaner" && user.role === "customer") {
      toast.info(
        "You already have a CLEANBAZ account. Head to your dashboard to upgrade to a Pro account — no need to sign up again.",
        { duration: 6000 }
      );
      nav("/dashboard", { replace: true });
    } else {
      nav(user.role === "admin" ? "/admin" : user.role === "cleaner" ? "/cleaner" : "/dashboard", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { email, password, full_name: fullName, role };
      if (refCode && role === "customer") payload.referral_code = refCode.trim().toUpperCase();
      const u = await signup(payload);
      toast.success(`Welcome, ${u.full_name}!`);
      if (next) nav(next);
      else nav(role === "cleaner" ? "/cleaner/onboarding" : "/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 400 && /already/i.test(detail)) {
        toast.error("An account with this email already exists. Please log in instead.", {
          action: { label: "Log in", onClick: () => nav(`/login?email=${encodeURIComponent(email)}`) },
        });
      } else {
        toast.error(detail || "Signup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <Card className="max-w-md w-full border-slate-200">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-2xl bg-teal-600 mx-auto flex items-center justify-center teal-glow">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-display font-bold text-2xl mt-4 text-slate-900">Create your account</h2>
          </div>
          <Tabs value={role} onValueChange={setRole} className="mb-4">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="customer" data-testid="signup-role-customer"><User className="w-3.5 h-3.5 mr-1" />Client</TabsTrigger>
              <TabsTrigger value="cleaner" data-testid="signup-role-cleaner"><Briefcase className="w-3.5 h-3.5 mr-1" />Cleaner</TabsTrigger>
            </TabsList>
          </Tabs>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Full name</Label>
              <Input required value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="signup-name" className="mt-1.5" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email" className="mt-1.5" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-password" className="mt-1.5" />
            </div>
            {role === "customer" && (
              <div>
                <Label>Referral code (optional) — get $10 off first booking</Label>
                <Input value={refCode} onChange={(e) => setRefCode(e.target.value)} data-testid="signup-refcode" placeholder="e.g. ALEX-A1B2C3" className="mt-1.5 font-mono" />
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11">
              {loading ? "Creating..." : "Create account"}
            </Button>
          </form>
          <div className="text-center text-sm text-slate-600 mt-5">
            Already have an account? <Link to="/login" className="text-teal-600 font-semibold" data-testid="signup-to-login">Sign in</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
